import axios from 'axios';
import moment from 'moment';
import debug from 'debug';
import { v4 as uuidv4 } from 'uuid';

import globals from '@/globals';
import router from '@/router';
import ParcelexApi from '@/services/parcelex';

import eventService from '../../services/event';
import CheckoutRedirectService from './checkoutRedirect.service';

import CheckoutLib from '../checkout/checkout.lib';
import getHandler from '../../lib/handleEvent';
import logger from '../../lib/logger';

import vtexLib from '../vtex/vtex.lib';
import nuvemshopLib from '../nuvemshop/nuvemshop.lib';

import UtilMixin from '../../mixins/util';
import deniedStrategy from '../checkout/utils/deniedStrategy';

let isHomolog = false;

const skeletonTimeout = globals().skeleton.checkout.timeout;

const log = debug('store:checkout');

async function getCheckoutData(initialData) {
  const { context } = initialData;

  const contextInitHandlers = {
    vtex: vtexLib,
    nuvemshop: nuvemshopLib,
  };

  const checkoutData = await contextInitHandlers[context].utilFunctions
    .getCheckoutData(initialData);

  return checkoutData;
}

async function checkIsHomolog({ order }) {
  if (order.products
    .find((el) => el.name === 'Lorem ipsum dolor sit amet, consectetur adipiscing elit')
  ) {
    isHomolog = true;
    ParcelexApi.defaults.baseURL = ParcelexApi.defaults.baseURL
      .replace('api.', 'api-homolog.');
  }
  log('isHomolog', isHomolog);
}

function putHomologInOptionId(optionsRes) {
  log('putHomologInOptionId', 'optionsResIn', optionsRes);
  if (isHomolog) {
    if (optionsRes && optionsRes.data && Array.isArray(optionsRes.data.downPaymentOptions)) {
      optionsRes.data.downPaymentOptions
        .forEach((dpOpt) => Array.isArray(dpOpt.options) && dpOpt.options.forEach((opt) => {
          // eslint-disable-next-line no-param-reassign
          opt._id = `homolog-${opt._id}`;
        }));
    }
  }
  log('putHomologInOptionId', 'optionsResOut', optionsRes);
}

const handleEvent = getHandler(log);

function getTotalProductPrice(products) {
  return products.reduce((acc, curr) => {
    const newVal = acc + curr.value * curr.amount;
    return newVal;
  }, 0);
}

function calculate(value, interest, installments) {
  const pv = value;
  // eslint-disable-next-line max-len
  const pmt = pv * ((((1 + interest) ** installments) * interest) / (((1 + interest) ** installments) - 1));
  return {
    installments,
    interest,
    pmt,
  };
}

function fixed2(raw) {
  return parseFloat(raw.toFixed(2));
}

function findInputErrors({ user, order }) {
  const { email, cpf, phone } = user;
  const { shipping } = order;

  return UtilMixin.methods.getInvalidInputs({
    email,
    cpf,
    phone,
    shipping,
  });
}

function checkValidationError({ errorMsg, validateField }) {
  if (!validateField) return false;

  const validateFieldsOf = {
    shipping: [
      'shipping',
      'zipCode',
      'addressStreet',
      'addressNumber',
      'addressComplement',
      'city',
      'state',
    ],
  };

  if (!validateFieldsOf[validateField]) return false;

  const fieldError = errorMsg.substring(errorMsg.indexOf('child'), errorMsg.indexOf('fails'))
    .trim()
    .replace(/"/g, '')
    .split(' ')[1];

  return validateFieldsOf[validateField].includes(fieldError);
}

export default {
  namespaced: true,
  state: {
    apiKey: '',
    sessionId: null,
    page: '',
    summarySkeleton: false,
    downPaymentValueSkeleton: false,
    installmentValueSkeleton: false,
    eventHandler: null,
    config: null,

    defaults: {
      interest: 0.03,
      installments: 12,
    },
    device: null,

    user: null,
    order: null,
    products: null,

    showScript: null,
    simulation: null,
    preApproved: null,
    totalProductPrice: null,
    totalAmount: null,
    preview: null,
    options: null,
    downPaymentOptions: null,
    installmentIndexSelected: null,
    downPaymentIndexSelected: 0,
    dueDateIndexSelected: 0,
    checkoutId: null,
    inputErrors: null,
    trackId: null,
    context: null,

    hasPreCondition: false,
    retailerName: 'default',
    vtexData: null,
    checkoutUrl: null,
    showApprovedPage: false,
    showFeedback: false,
    isLoading: false,

    checkoutData: null,
    hasGracePeriod: false,
  },
  actions: {
    reload({ state, dispatch }, newState) {
      if (newState && newState.retailerName) {
        dispatch('theme/setTheme', newState.retailerName, { root: true });
      }

      Object.keys(newState)
        .forEach((key) => {
          state[key] = newState[key];
        });
    },
    async windowResized({ state }, { width, height }) {
      handleEvent({ state, ev: 'onWindowResize', data: { width, height } });
    },
    async checkoutValidate({ state }, checkoutId) {
      if (!state.checkoutId
      || !checkoutId
      || state.checkoutId !== checkoutId) {
        throw new Error('wrong checkout cache');
      }
    },
    async error({ state }) {
      handleEvent({ state, ev: 'onError' });
    },
    async resetLoading({ commit }) {
      commit('toggleSummarySkeleton', false);
      commit('toggleDownPaymentValueSkeleton', false);
      commit('toggleInstallmentValueSkeleton', false);
    },
    async ready({ commit, state }) {
      handleEvent({ state, ev: 'onReady' });
      commit('toggleSummarySkeleton', false);
      commit('toggleDownPaymentValueSkeleton', false);
      commit('toggleInstallmentValueSkeleton', false);
    },
    async init({ rootState, dispatch, commit, state }, initData) {
      const checkoutData = await getCheckoutData(initData);

      dispatch('setContext', initData.context);

      commit('setCheckoutData', checkoutData);
      commit('setCheckoutUrl', checkoutData.checkoutUrl);
      commit('setConfirmUrl', checkoutData.confirmUrl);

      commit('setHasPreCondition', false);

      dispatch('theme/setTheme', checkoutData.retailerName || 'default', { root: true });

      checkIsHomolog(checkoutData);

      try {
        const errorFound = findInputErrors(checkoutData);
        log('init', state);
        log('data', checkoutData);

        const totalProductPrice = getTotalProductPrice(checkoutData.order.products);
        commit('setInitData', {
          ...checkoutData,
          apiKey: initData.apiKey,
          totalProductPrice: Number.isNaN(totalProductPrice) ? 0 : totalProductPrice,
          sessionId: uuidv4(),
        });

        if (errorFound && errorFound.length > 0) {
          commit('setInputErrors', errorFound);
          commit('isDenied', true);
          return;
        }
        commit('setInputErrors', null);

        const { order, user } = checkoutData;
        const { products } = order;
        const product = (products && products[0]) || {};

        const currentTrackId = rootState.track && rootState.track.id;
        const trackId = checkoutData.trackId || currentTrackId;

        const { data } = await CheckoutRedirectService
          .preview({
            apiKey: initData.apiKey,
            cpf: user.cpf,
            phone: user.phone,
            email: user.email,
            shipping: order.shipping.value,
            totalProductPrice,
            checkoutId: checkoutData.checkoutId || null,
            skuNumber: String(product.sku || product.id),
            productName: product.name,
            productDescription: product.description,
            seller: product.seller,
            productUrl: product.url,
            productImage: product.image,
            categoryName: product.category,
            categoryId: product.categoryId,
            bypassEvent: true,
            trackId,
          });

        log('preview', data);
        commit('setPreview', data);

        dispatch('track/setId', { id: data.trackId }, { root: true });

        if (data.preApproved) {
          commit('setHasPreCondition', true);
          const optionsRes = await CheckoutRedirectService.options({
            apiKey: state.apiKey,
            env: state.env,
            device: state.device,
            user: state.user,
            order: state.order,
            sessionId: state.sessionId,
            trackId: rootState.track && rootState.track.id,
            hasGracePeriod: initData.hasGracePeriod || false,
            paymentAttemptId: (state.checkoutData && state.checkoutData.paymentAttemptId) || null,
          });
          dispatch('setHasGracePeriod', {
            hasGracePeriod: optionsRes.data.hasGracePeriod || false,
          });
          commit('setCheckoutId', optionsRes.data.checkoutId);
          commit('setShowApprovedPage', true);
          commit('setShowFeedback', true);
          putHomologInOptionId(optionsRes);
          commit('setOptions', { ...optionsRes.data, sessionId: state.sessionId });
        } else if (data.showScript === false) {
          commit('setHasPreCondition', true);
          commit('setOptions', {
            result: 'NO_ACTION_PREVIEW',
            refusalReason: data.refusalReason,
          });
        }
      } catch (error) {
        logger.error(error);
        if (error && error.response && error.response.status === 400) {
          if (error.response.data === 'invalid email') {
            commit('setInputErrors', ['email']);
          } else if (checkValidationError({ errorMsg: error.response.data, validateField: 'shipping' })) {
            commit('setInputErrors', ['shipping']);
          } else {
            commit('setInputErrors', ['genericError']);
          }
        } else {
          throw error;
        }
      }
    },
    emitEvent({ rootState, state }, {
      type,
      page = 'checkout',
    }) {
      const { downPaymentOptions, installmentIndexSelected } = state;
      const index = installmentIndexSelected || 0;
      let optionId = null;
      if (downPaymentOptions && index >= 0) {
        const downPayment = downPaymentOptions[index];
        if (downPayment && downPayment.options && Array.isArray(downPayment.options)) {
          optionId = downPayment.options[installmentIndexSelected]
           && downPayment.options[installmentIndexSelected]._id;
        }
      }

      eventService.logCheckoutEvents({
        type,
        data: {
          ...state,
          page,
          optionId,
          trackId: rootState.track && rootState.track.id,
        },
      }).catch(logger.error);
    },
    async pay({ state, getters, dispatch }) {
      state.isLoading = true;
      const optionId = getters.installmentSelected._id;
      const confirmUrlQuery = new URLSearchParams({ optionId }).toString();

      try {
        const { data } = await axios.get(`${state.confirmUrl}?${confirmUrlQuery}`);
        dispatch('checkout/setFallbackData', {
          downPaymentOptions: state.downPaymentOptions,
          downPaymentIndexSelected: state.downPaymentIndexSelected,
          installmentIndexSelected: state.installmentIndexSelected,
          checkoutId: state.checkoutId,
        }, { root: true });

        const onboardingQuery = new URLSearchParams({
          onboardingToken: data.onboardingToken,
          transactionId: data.transactionId,
        }).toString();

        window.location.href = `/onboarding?${onboardingQuery}`;
      } catch (err) {
        logger.error(err);
        router.replace({ name: 'checkout-error' });
      }
    },
    async goToPage({ commit }, page) {
      commit('goToPage', page);
    },
    async getOptions({ rootState, state, commit, dispatch }) {
      try {
        const { data } = await CheckoutRedirectService.options({
          apiKey: state.apiKey,
          env: state.env,
          device: state.device,
          user: state.user,
          order: state.order,
          sessionId: state.sessionId,
          trackId: rootState.track && rootState.track.id,
          hasGracePeriod: state.hasGracePeriod,
          paymentAttemptId: (state.checkoutData && state.checkoutData.paymentAttemptId) || null,
        });
        commit('setCheckoutId', data.checkoutId);
        putHomologInOptionId({ data });
        const {
          trackId,
        } = data;

        const currentTrackId = rootState.track && rootState.track.id;
        if (trackId && trackId !== currentTrackId) {
          dispatch('track/setId', { id: trackId }, { root: true });
        }
        commit('setOptions', data);
      } catch (error) {
        if (error && error.response && error.response.status === 400) {
          if (checkValidationError({ errorMsg: error.response.data, validateField: 'shipping' })) {
            commit('setInputErrors', ['shipping']);
          } else {
            commit('setInputErrors', ['genericError']);
          }
        } else {
          throw error;
        }
      }
    },
    async setContext({ commit }, context) {
      commit('setContext', context);
    },
    setInstallmentIndex({ commit, state }, index) {
      const {
        downPaymentOptions,
        downPaymentIndexSelected,
        dueDateIndexSelected,
        hasGracePeriod,
      } = state;
      if (downPaymentOptions && downPaymentIndexSelected >= 0 && index >= 0) {
        if (downPaymentOptions) {
          const downPayment = downPaymentOptions[downPaymentIndexSelected];
          if (!downPayment) return;

          if (hasGracePeriod && downPayment.dueDateOptions && downPayment.dueDateOptions.length) {
            const dueDateOption = downPayment.dueDateOptions[dueDateIndexSelected];
            const installment = dueDateOption.installmentOptions[index];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          } else if (downPayment.options && downPayment.options.length) {
            const installment = downPayment.options[index];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          }
        }
      }
      const { summarySkeleton } = state;
      if (!summarySkeleton) {
        commit('toggleSummarySkeleton', true);
      }
      setTimeout(() => {
        commit('toggleSummarySkeleton', false);
      }, skeletonTimeout);
      commit('setInstallmentIndex', index);
    },
    setDownPaymentIndex({ commit, state }, index) {
      const {
        downPaymentOptions,
        installmentIndexSelected,
        dueDateIndexSelected,
        hasGracePeriod,
      } = state;
      if (downPaymentOptions && index >= 0) {
        const downPayment = downPaymentOptions[index];
        handleEvent({ state, ev: 'onDownPaymentChanged', data: { downPayment } });

        if (typeof installmentIndexSelected === 'number' && installmentIndexSelected >= 0) {
          if (hasGracePeriod && downPayment.dueDateOptions && downPayment.dueDateOptions.length) {
            const dueDateOption = downPayment.dueDateOptions[dueDateIndexSelected];
            const installment = dueDateOption.installmentOptions[installmentIndexSelected];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          } else if (downPayment.options && downPayment.options.length) {
            const installment = downPayment.options[installmentIndexSelected];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          }
        }
      }
      const { summarySkeleton } = state;
      if (!summarySkeleton) {
        commit('toggleSummarySkeleton', true);
        commit('toggleInstallmentValueSkeleton', true);
      }
      setTimeout(() => {
        commit('toggleSummarySkeleton', false);
        commit('toggleInstallmentValueSkeleton', false);
      }, skeletonTimeout);
      commit('setDownPaymentIndex', index);
    },
    setDueDateIndex({ commit, state }, index) {
      const {
        downPaymentOptions,
        downPaymentIndexSelected,
        installmentIndexSelected,
      } = state;
      if (downPaymentOptions && downPaymentIndexSelected >= 0 && index >= 0) {
        const downPayment = downPaymentOptions[downPaymentIndexSelected];
        if (downPayment && downPayment.dueDateOptions) {
          const dueDateOption = downPayment.dueDateOptions[index];
          handleEvent({ state, ev: 'onDueDateChanged', data: { dueDateOption } });

          if (typeof installmentIndexSelected === 'number'
            && installmentIndexSelected >= 0) {
            const installment = dueDateOption.installmentOptions[installmentIndexSelected];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          }
        }
      }

      const { summarySkeleton } = state;
      if (!summarySkeleton) {
        commit('toggleSummarySkeleton', true);
        commit('toggleInstallmentValueSkeleton', true);
      }
      setTimeout(() => {
        commit('toggleSummarySkeleton', false);
        commit('toggleInstallmentValueSkeleton', false);
      }, skeletonTimeout);
      commit('setDueDateIndex', index);
    },
    setHasGracePeriod({ commit }, hasGracePeriod) {
      commit('setHasGracePeriod', hasGracePeriod);
    },
  },
  mutations: {
    setHasPreCondition(state, hasPreCondition) {
      state.hasPreCondition = hasPreCondition;
    },
    toggleSummarySkeleton(state, summarySkeleton) {
      state.summarySkeleton = summarySkeleton;
    },
    toggleDownPaymentValueSkeleton(state, downPaymentValueSkeleton) {
      state.downPaymentValueSkeleton = downPaymentValueSkeleton;
    },
    toggleInstallmentValueSkeleton(state, installmentValueSkeleton) {
      state.installmentValueSkeleton = installmentValueSkeleton;
    },
    setShipping(state, shipping) {
      state.order.shipping = shipping;
    },
    setSessionId(state, sessionId) {
      state.sessionId = sessionId;
    },
    setDownPaymentOptions(state, downPaymentOptions) {
      state.downPaymentOptions = downPaymentOptions;
    },
    goToPage(state, page) {
      handleEvent({ state, ev: 'onChangePage', data: { page } });
      router.replace({ name: page }).catch((err) => log('router error', err));
      state.page = page;
    },
    setContext(state, context) {
      const { config, eventHandler } = CheckoutLib.getContextVariables(context);
      state.eventHandler = eventHandler;
      state.config = config;
      state.context = context;
    },
    setDownPaymentIndex(state, index) {
      state.downPaymentIndexSelected = index;
    },
    setInstallmentIndex(state, index) {
      state.installmentIndexSelected = index;
    },
    setInitData(state, {
      apiKey,
      device,
      user,
      totalProductPrice,
      order,
      checkoutId,
      sessionId,
      retailerName,
    }) {
      state.apiKey = apiKey;
      state.device = device;
      state.user = user;
      state.order = order;
      state.products = order.products;
      state.totalProductPrice = totalProductPrice;
      state.totalAmount = totalProductPrice + order.shipping.value;
      state.checkoutId = checkoutId;
      state.sessionId = sessionId;
      state.options = null;
      state.retailerName = retailerName || 'default';
    },
    setPreview(state, preview) {
      handleEvent({ state, ev: 'onPreviewChanged', data: { preview } });
      state.preview = preview;
      state.showScript = preview.showScript;
      state.simulation = preview.simulation;
      state.preApproved = preview.preApproved;
    },
    setOptions(state, options) {
      handleEvent({ state, ev: 'onOptionsChanged', data: { options } });
      state.checkoutId = options.checkoutId;
      state.options = options;
      state.downPaymentOptions = options.downPaymentOptions;
      state.hasGracePeriod = options.hasGracePeriod;
    },
    setPrice(state, price) {
      state.price = price;
    },
    setInputErrors(state, inputErrors) {
      state.inputErrors = inputErrors;
    },
    setVtexData(state, vtexData) {
      state.vtexData = vtexData;
    },
    setCheckoutData(state, checkoutData) {
      state.checkoutData = checkoutData;
    },
    setCheckoutUrl(state, checkoutUrl) {
      state.checkoutUrl = checkoutUrl.includes('https://')
        ? checkoutUrl : `https://${checkoutUrl}`;
    },
    setConfirmUrl(state, confirmUrl) {
      state.confirmUrl = confirmUrl;
    },
    setShowApprovedPage(state, showApprovedPage) {
      state.showApprovedPage = showApprovedPage;
    },
    setShowFeedback(state, showFeedback) {
      state.showFeedback = showFeedback;
    },
    setCheckoutId(state, checkoutId) {
      state.checkoutId = checkoutId;
    },
    setHasGracePeriod(state, { hasGracePeriod }) {
      state.hasGracePeriod = hasGracePeriod;
    },
    setDueDateIndex(state, index) {
      state.dueDateIndexSelected = index;
    },
  },
  getters: {
    downPaymentSelected({ downPaymentOptions, downPaymentIndexSelected }) {
      if (!downPaymentOptions) return null;
      return downPaymentOptions[downPaymentIndexSelected];
    },
    installmentSelected(state, getters) {
      const { downPaymentSelected } = getters;
      const { installmentIndexSelected, dueDateIndexSelected, hasGracePeriod } = state;
      if (!downPaymentSelected) return null;
      if (hasGracePeriod) {
        if (!downPaymentSelected.dueDateOptions) return null;
        return downPaymentSelected
          .dueDateOptions[dueDateIndexSelected]
          .installmentOptions[installmentIndexSelected];
      }
      if (!downPaymentSelected.options) return null;
      return downPaymentSelected.options[installmentIndexSelected];
    },
    dueDateSelected: (state, getters) => {
      const { downPaymentSelected } = getters;
      const { dueDateIndexSelected } = state;
      if (!downPaymentSelected) return null;
      if (!downPaymentSelected.dueDateOptions) return null;
      return downPaymentSelected.dueDateOptions[dueDateIndexSelected];
    },
    dateOptions: ({ downPaymentOptions }) => {
      const [firstOption] = downPaymentOptions;
      return firstOption.dueDateOptions.map(
        (dueDateOption) => moment(dueDateOption.gracePeriodDueDate, 'YYYY-MM-DD')
          .format('D-MMM')
          .toUpperCase()
          .split('-'),
      );
    },
    simulation: ({ defaults, preview, totalAmount }) => {
      const simulatedInterest = preview
        && preview.simulation && preview.simulation.interest;
      const installmentsNumber = preview
      && preview.simulation && preview.simulation.installmentsNumber;
      const {
        installments,
        pmt,
      } = calculate(
        totalAmount,
        simulatedInterest || defaults.interest,
        installmentsNumber || defaults.installments,
      );
      return {
        pmt: Number.isNaN(pmt) ? '-' : fixed2(pmt),
        totalValue: Number.isNaN(pmt) || Number.isNaN(installments)
          ? '-' : fixed2(pmt * installments),
        numberOfInstallments: Number.isNaN(installments) ? '-' : installments,
      };
    },
    hasOptions: (state) => !!state.options,
    hasCheckoutData: (state) => !!state.checkoutData,
    isApproved: (state) => {
      if (!state.options) return null;
      return state.options.result === 'APPROVED';
    },
    isDenied: (state) => {
      if (!state.options && !state.inputErrors) return null;
      return !!(state.inputErrors && state.inputErrors.length >= 1) || state.options.result !== 'APPROVED';
    },
    ccbUrl: (state, getters) => {
      const { user, order } = state;
      const { installmentSelected } = getters;
      const { shipping } = order || {};

      const basicPath = 'https://cdn.parcelex.com.br/minha-ccb.html';
      if (!user || !order || !installmentSelected || !shipping) return basicPath;

      const {
        downPayment,
        downPaymentValue,
        financedAmount,
        totalAmount,
        totalIof,
        interestRatePM,
        interestRatePA,
        cetPA,
        paymentInstallments,
        numberOfInstallments,
        priceDetails,
        createdAt,
        tcValue,
      } = installmentSelected;

      const installments = [];

      const installmentTimes = downPayment
        ? numberOfInstallments + 1
        : numberOfInstallments;

      if (downPayment) {
        installments.push({
          amortization: downPaymentValue,
          interest: 0,
          total: downPaymentValue,
          dueDate: moment(createdAt).add(1, 'days').format('DD/MM/YYYY'),
        });
      }

      for (let i = downPayment ? 1 : 0; i < installmentTimes; i += 1) {
        const priceDetail = priceDetails[i];
        const { amortization } = priceDetail;
        installments.push({
          amortization,
          interest: paymentInstallments - amortization,
          total: paymentInstallments,
          dueDate: moment(createdAt).add(i + (downPayment ? 0 : 1), 'months').format('DD/MM/YYYY'),
        });
      }

      const splitedName = (user.name && user.name.split(' ')) || null;
      const params = {
        name: splitedName ? `${splitedName[0]} ${splitedName[1]}` : '',
        cpf: user.cpf,

        zipCode: shipping.zipCode,
        address: `${shipping.street}, ${shipping.number}`,
        city: shipping.city,
        uf: shipping.state,

        firstInstallment: installments[0].dueDate,
        lastInstallment: installments[installments.length - 1].dueDate,
        numberOfInstallments,
        totalAmount,
        totalIof,
        financedAmount,
        tc: tcValue || 0,
        pmt: paymentInstallments,
        percentage: interestRatePA,
        interestRatePM,
        interestRatePA,
        cetPA,
        installments: installments
          .map(({
            amortization, interest, total, dueDate,
          }) => `${[amortization, interest, total, dueDate].join(', ')}] `)
          .join(', '),
      };

      const query = new URLSearchParams(params);
      return `${basicPath}?${query}`;
    },
    titleDeniedMessage: ({ options }) => deniedStrategy.titleDeniedMessage({ options }),
    infoDeniedMessage: ({ options }) => deniedStrategy.infoDeniedMessage({ options }),
  },
};
